// The launch images of card 28 (brand.md §9). iOS shows no launch screen
// of its own for a web app on the home screen: it shows the image of an
// `apple-touch-startup-image` link whose media query matches the screen,
// and a plain screen when no link matches. Each image must have the exact
// pixel size of the screen in its orientation.
//
// The set is every screen size of an iPhone and an iPad that runs iOS 16.4
// or later, the first release that gives a web app on the home screen its
// push notifications (player-reach.md §3.5). An iPhone takes a portrait image;
// an iPad takes a portrait and a landscape image, because the game turns
// with it.
//
// The module is pure. `launch.test.ts` holds the link block of
// `client/pages/index.html.tmpl` to `startupLinks()`, so the page and the
// files cannot drift apart.

/** One screen size: the CSS pixels in portrait, and the device pixel
 *  ratio. `devices` names the models, for the page and the comments. */
export interface Screen {
  readonly w: number;
  readonly h: number;
  readonly dpr: number;
  readonly ipad: boolean;
  readonly devices: string;
}

export const SCREENS: readonly Screen[] = [
  { w: 375, h: 667, dpr: 2, ipad: false, devices: 'iPhone 8, SE (2nd, 3rd)' },
  { w: 414, h: 736, dpr: 3, ipad: false, devices: 'iPhone 8 Plus' },
  { w: 375, h: 812, dpr: 3, ipad: false, devices: 'iPhone X, XS, 11 Pro, 12 mini, 13 mini' },
  { w: 414, h: 896, dpr: 2, ipad: false, devices: 'iPhone XR, 11' },
  { w: 414, h: 896, dpr: 3, ipad: false, devices: 'iPhone XS Max, 11 Pro Max' },
  { w: 390, h: 844, dpr: 3, ipad: false, devices: 'iPhone 12, 12 Pro, 13, 13 Pro, 14, 16e' },
  { w: 428, h: 926, dpr: 3, ipad: false, devices: 'iPhone 12 Pro Max, 13 Pro Max, 14 Plus' },
  { w: 393, h: 852, dpr: 3, ipad: false, devices: 'iPhone 14 Pro, 15, 15 Pro, 16' },
  { w: 430, h: 932, dpr: 3, ipad: false, devices: 'iPhone 14 Pro Max, 15 Plus, 15 Pro Max, 16 Plus' },
  { w: 402, h: 874, dpr: 3, ipad: false, devices: 'iPhone 16 Pro, 17, 17 Pro' },
  { w: 420, h: 912, dpr: 3, ipad: false, devices: 'iPhone Air' },
  { w: 440, h: 956, dpr: 3, ipad: false, devices: 'iPhone 16 Pro Max, 17 Pro Max' },
  { w: 744, h: 1133, dpr: 2, ipad: true, devices: 'iPad mini (6th, 7th)' },
  { w: 768, h: 1024, dpr: 2, ipad: true, devices: 'iPad (5th, 6th), iPad mini (5th), iPad Pro 9.7"' },
  { w: 810, h: 1080, dpr: 2, ipad: true, devices: 'iPad (7th, 8th, 9th)' },
  { w: 820, h: 1180, dpr: 2, ipad: true, devices: 'iPad (10th, A16), iPad Air (4th, 5th), iPad Air 11" (M2, M3)' },
  { w: 834, h: 1112, dpr: 2, ipad: true, devices: 'iPad Air (3rd), iPad Pro 10.5"' },
  { w: 834, h: 1194, dpr: 2, ipad: true, devices: 'iPad Pro 11" (1st to 4th)' },
  { w: 834, h: 1210, dpr: 2, ipad: true, devices: 'iPad Pro 11" (M4, M5)' },
  { w: 1024, h: 1366, dpr: 2, ipad: true, devices: 'iPad Pro 12.9", iPad Air 13" (M2, M3)' },
  { w: 1032, h: 1376, dpr: 2, ipad: true, devices: 'iPad Pro 13" (M4, M5)' },
];

export type Orientation = 'portrait' | 'landscape';

/** One launch image: its pixel size, its file and its media query. */
export interface LaunchImage {
  readonly id: string;
  readonly file: string;
  /** The size in device pixels, in its orientation. */
  readonly w: number;
  readonly h: number;
  readonly orientation: Orientation;
  readonly screen: Screen;
  readonly media: string;
}

/** The stacked lockup of a launch image: its sign box is this share of the
 *  short side of the screen, and never more than `SIGN_MAX_CSS` CSS px. */
export const SIGN_SHARE = 0.32;
export const SIGN_MAX_CSS = 168;

/** The sign box of a launch image, in device pixels. */
export function launchSignPx(l: LaunchImage): number {
  const s = l.screen;
  return Math.round(Math.min(SIGN_SHARE * Math.min(s.w, s.h), SIGN_MAX_CSS) * s.dpr);
}

function image(s: Screen, o: Orientation): LaunchImage {
  const pw = s.w * s.dpr;
  const ph = s.h * s.dpr;
  const [w, h] = o === 'portrait' ? [pw, ph] : [ph, pw];
  return {
    id: `launch-${w}x${h}`,
    file: `launch-${w}x${h}.png`,
    w, h, orientation: o, screen: s,
    media: `(device-width: ${s.w}px) and (device-height: ${s.h}px) and (-webkit-device-pixel-ratio: ${s.dpr}) and (orientation: ${o})`,
  };
}

/** Every launch image: each screen in portrait, and each iPad in landscape
 *  as well. */
export function launchImages(): LaunchImage[] {
  return SCREENS.flatMap((s) => (s.ipad ? [image(s, 'portrait'), image(s, 'landscape')] : [image(s, 'portrait')]));
}

/** The link block of `client/pages/index.html.tmpl`: one
 *  `apple-touch-startup-image` link a launch image, at the indent of the
 *  head. */
export function startupLinks(indent = '    '): string {
  return launchImages()
    .map((l) => `${indent}<link rel="apple-touch-startup-image" href="/brand/launch/${l.file}" media="${l.media}" />`)
    .join('\n');
}

/**
 * The launch image that fits a viewport best, for the cover of a world
 * that loads (`ui/dockin.ts`): the images of its orientation, and of those
 * the one whose shape is nearest to the viewport. A portrait viewport takes
 * a portrait image and a landscape viewport takes an iPad landscape image,
 * and the cover takes the size of its sign (`coverSignCss`).
 */
export function launchImageFor(w: number, h: number): LaunchImage {
  const o: Orientation = w > h ? 'landscape' : 'portrait';
  const pool = launchImages().filter((l) => l.orientation === o);
  const want = w / h;
  let best = pool[0];
  for (const l of pool) {
    if (Math.abs(Math.log(l.w / l.h / want)) < Math.abs(Math.log(best.w / best.h / want))) best = l;
  }
  return best;
}

/**
 * The sign box of the black cover of the game page, in CSS px, for a
 * viewport of `vw` x `vh` CSS px (brand.md §11.2). It is the size that the
 * launch image of `launchImageFor` gives the sign when it is scaled to
 * cover the viewport. The cover draws the lockup at this size on a canvas
 * (`coverLockupCanvas` of `lockupdraw.ts`) and does not scale the raster,
 * so the size does not cost the sharpness.
 */
export function coverSignCss(vw: number, vh: number): number {
  const l = launchImageFor(vw, vh);
  return launchSignPx(l) * Math.max(vw / l.w, vh / l.h);
}
